import React, { useEffect, useState } from "react";
import { getDownloadURL, ref, StorageError } from "@firebase/storage";
import { useStorage } from "reactfire";

import { useTranslator } from "@/i18n/useTranslator";

import { classNames } from "../utils/classNames";
import { DsButton, DsIcon, DsModal, DsText } from "./ui";

type SingleImagePropsType = {
  path: string;
};

type ImgSliderFromPathsPropsType = {
  paths?: string[];
  className?: string;
};

type ImageDisplayPropsType = {
  path: string;
  className?: string;
  onClick?: () => void;
};

const ImageDisplay = ({ path, className, onClick }: ImageDisplayPropsType) => {
  const { t } = useTranslator();
  const storage = useStorage();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setHasError(false);
    setUrl(null);

    getDownloadURL(ref(storage, path))
      .then((downloadUrl: string) => {
        if (isMounted) {
          setUrl(downloadUrl);
          setIsLoading(false);
        }
      })
      .catch((error: StorageError) => {
        if (isMounted) {
          console.debug(`Failed to load image ${path}:`, error);
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [path, storage]);

  if (hasError) {
    return null;
  }

  if (isLoading) {
    return <DsText variant="body">{t("loading")}</DsText>;
  }

  if (!url) {
    return null;
  }

  return (
    <img
      className={classNames(className, "cursor-pointer")}
      src={url}
      alt={t("image.from_storage")}
      onClick={onClick}
    />
  );
};

const SingleImage = ({ path }: SingleImagePropsType) => {
  if (!path) return null;

  const segments = path.split("/");
  return (
    <div className="relative inline-block align-bottom py-2 px-5 text-left overflow-hidden transform transition-all sm:align-middle w-full">
      <ImageDisplay
        path={path}
        className="w-full max-h-[80vh] object-contain"
      />
      <DsText variant="body" className="text-center mt-2">
        {segments[segments.length - 1].split("-")[0]}
      </DsText>
    </div>
  );
};

export const ImgSliderFromPaths = ({
  paths = [],
  className,
}: ImgSliderFromPathsPropsType) => {
  const { t } = useTranslator();
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ensure paths is an array and contains only valid strings
  const validPaths = Array.isArray(paths)
    ? paths.filter(
        (p): p is string => typeof p === "string" && p.trim().length > 0,
      )
    : [];

  // Reset currentIndex if it's out of bounds
  useEffect(() => {
    if (currentIndex >= validPaths.length) {
      setCurrentIndex(0);
    }
  }, [validPaths.length, currentIndex]);

  // Early return for no valid paths
  if (!validPaths.length) return null;
  // If we only have one valid path, just show the image without navigation
  if (validPaths.length === 1) {
    return (
      <>
        <ImageDisplay
          path={validPaths[0]}
          className={className}
          onClick={() => setIsOpen(true)}
        />
        <DsModal
          isOpen={isOpen}
          onDismiss={() => setIsOpen(false)}
          panelClassName="md:w-[95%] md:ml-[200px] md:mr-2 p-4">
          <div className="flex flex-col items-center justify-center md:gap-2 gap-1">
            <DsIcon
              icon="Close"
              onClick={() => setIsOpen(false)}
              width={33}
              height={33}
              className="cursor-pointer rounded self-end p-1"
            />
            <SingleImage path={validPaths[0]} />
          </div>
        </DsModal>
      </>
    );
  }

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(
      currentIndex === 0 ? validPaths.length - 1 : currentIndex - 1,
    );
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(
      currentIndex === validPaths.length - 1 ? 0 : currentIndex + 1,
    );
  };

  return (
    <>
      {validPaths.map((path, index) => (
        <ImageDisplay
          key={path}
          path={path}
          className={className}
          onClick={() => {
            setCurrentIndex(index);
            setIsOpen(true);
          }}
        />
      ))}
      <DsModal
        isOpen={isOpen}
        onDismiss={() => setIsOpen(false)}
        panelClassName="md:w-[95%] md:ml-[200px] md:mr-2 p-4">
        <div className="flex flex-col items-center justify-center md:gap-2 gap-1">
          <DsIcon
            icon="Close"
            onClick={() => setIsOpen(false)}
            width={33}
            height={33}
            className="cursor-pointer rounded self-end p-1"
          />
          <SingleImage path={validPaths[currentIndex]} />
          <div className="flex gap-4">
            <DsButton variant="secondary" onClick={handlePrevious}>
              {t("image_slide.prev")}
            </DsButton>
            {currentIndex - 1 < validPaths.length && (
              <DsButton variant="secondary" onClick={handleNext}>
                {t("image_slide.next")}
              </DsButton>
            )}
          </div>
        </div>
      </DsModal>
    </>
  );
};
